import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '@/lib/adminApi';

const EPK_THEMES = [
  { id: 'minimal-light', label: 'Minimal Light' },
  { id: 'classical-gold', label: 'Classical Gold' },
  { id: 'folk-warm', label: 'Folk Warm' },
  { id: 'modern-dark', label: 'Modern Dark' },
  { id: 'bold-stage', label: 'Bold Stage' },
];

const EPK_SECTIONS = [
  { id: 'hero', label: 'Hero' },
  { id: 'about', label: 'About' },
  { id: 'highlights', label: 'Portfolio Highlights' },
  { id: 'services', label: 'Services' },
  { id: 'stats', label: 'Stats' },
  { id: 'cta', label: 'CTA' },
];

const AdminLivingEpk = () => {
  const [search, setSearch] = useState('');
  const [artists, setArtists] = useState([]);
  const [selectedArtistId, setSelectedArtistId] = useState('');
  const [epkData, setEpkData] = useState(null);
  const [theme, setTheme] = useState('minimal-light');
  const [lockedSections, setLockedSections] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(true);
  const [loadingEpk, setLoadingEpk] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [message, setMessage] = useState('');

  const selectedArtist = useMemo(
    () => artists.find((artist) => artist.id === selectedArtistId) || null,
    [artists, selectedArtistId],
  );

  const loadArtists = async () => {
    setLoadingArtists(true);
    try {
      const response = await adminApi.get('/admin/epk/artists', { params: { search } });
      const nextArtists = response.data?.artists || [];
      setArtists(nextArtists);
      if (!selectedArtistId && nextArtists[0]?.id) setSelectedArtistId(nextArtists[0].id);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not load artists for EPK.');
    } finally {
      setLoadingArtists(false);
    }
  };

  const loadEpk = async (artistId = selectedArtistId) => {
    if (!artistId) return;
    setLoadingEpk(true);
    try {
      const response = await adminApi.get(`/admin/epk/${artistId}`);
      setEpkData(response.data);
      setTheme(response.data?.settings?.theme || 'minimal-light');
      setLockedSections(response.data?.settings?.locked_sections || []);
      setMessage('');
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not load EPK.');
    } finally {
      setLoadingEpk(false);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(loadArtists, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (selectedArtistId) loadEpk(selectedArtistId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedArtistId]);

  const saveSettings = async () => {
    if (!selectedArtistId) return;
    setBusyAction('settings');
    try {
      await adminApi.patch(`/admin/epk/${selectedArtistId}/settings`, {
        theme,
        locked_sections: lockedSections,
      });
      setMessage('EPK settings saved.');
      await loadEpk(selectedArtistId);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not save EPK settings.');
    } finally {
      setBusyAction('');
    }
  };

  const regenerate = async () => {
    if (!selectedArtistId) return;
    setBusyAction('regenerate');
    try {
      await saveSettings();
      await adminApi.post(`/admin/epk/${selectedArtistId}/regenerate`);
      setMessage('EPK regenerated as a draft. Review and publish when ready.');
      await loadEpk(selectedArtistId);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not regenerate EPK.');
    } finally {
      setBusyAction('');
    }
  };

  const publish = async () => {
    if (!selectedArtistId) return;
    setBusyAction('publish');
    try {
      await adminApi.post(`/admin/epk/${selectedArtistId}/publish`);
      setMessage('EPK published.');
      await Promise.all([loadArtists(), loadEpk(selectedArtistId)]);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not publish EPK.');
    } finally {
      setBusyAction('');
    }
  };

  const rollback = async (versionId) => {
    if (!selectedArtistId || !versionId) return;
    setBusyAction(`rollback:${versionId}`);
    try {
      await adminApi.post(`/admin/epk/${selectedArtistId}/rollback`, null, { params: { version_id: versionId } });
      setMessage('EPK rolled back to the selected version.');
      await loadEpk(selectedArtistId);
    } catch (error) {
      setMessage(error.response?.data?.detail || 'Could not rollback EPK.');
    } finally {
      setBusyAction('');
    }
  };

  const toggleLock = (sectionId) => {
    setLockedSections((current) =>
      current.includes(sectionId) ? current.filter((item) => item !== sectionId) : [...current, sectionId],
    );
  };

  const current = epkData?.current || {};
  const blueprint = current.blueprint || {};
  const sections = blueprint.sections || {};
  const hero = sections.hero || {};
  const about = sections.about || {};
  const highlights = sections.highlights || {};
  const publishedSlug = selectedArtist?.slug || blueprint.slug;

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm lg:p-8">
        <p className="text-xs font-black uppercase tracking-[0.35em] text-[#FF6F00]">Living EPK</p>
        <h1 className="mt-3 text-3xl font-black text-gray-950">Artist EPK control center</h1>
        <p className="mt-3 max-w-3xl text-gray-600">
          Search artists, generate a living press kit, publish approved versions, rollback older versions, and lock sections that should not be rewritten.
        </p>
      </section>

      {message && <div className="rounded-2xl border border-orange-100 bg-orange-50 px-5 py-3 font-bold text-orange-800">{message}</div>}

      <div className="grid gap-6 xl:grid-cols-[360px_1fr]">
        <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
          <label className="text-xs font-black uppercase tracking-[0.25em] text-gray-500">Select artist</label>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by artist, city, category"
            className="mt-3 w-full rounded-2xl border border-gray-200 px-4 py-3"
          />
          <div className="mt-4 max-h-[560px] space-y-2 overflow-y-auto pr-1">
            {loadingArtists ? (
              <p className="rounded-2xl bg-gray-50 p-4 font-bold text-gray-500">Loading artists...</p>
            ) : artists.length === 0 ? (
              <p className="rounded-2xl bg-gray-50 p-4 font-bold text-gray-500">No artists found.</p>
            ) : (
              artists.map((artist) => (
                <button
                  key={artist.id}
                  type="button"
                  onClick={() => setSelectedArtistId(artist.id)}
                  className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition ${
                    selectedArtistId === artist.id ? 'border-[#FF6F00] bg-orange-50' : 'border-gray-100 bg-white hover:bg-gray-50'
                  }`}
                >
                  {artist.image_url ? (
                    <img src={artist.image_url} alt="" className="h-12 w-12 rounded-xl object-cover" />
                  ) : (
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 font-black">K</span>
                  )}
                  <span className="min-w-0">
                    <span className="block truncate font-black text-gray-950">{artist.name}</span>
                    <span className="block truncate text-xs font-semibold text-gray-500">{artist.subcategory || artist.category || 'Artist'} {artist.city ? `- ${artist.city}` : ''}</span>
                    <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-black ${artist.epk_published ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                      {artist.epk_published ? 'Published EPK' : 'No published EPK'}
                    </span>
                  </span>
                </button>
              ))
            )}
          </div>
        </section>

        <section className="space-y-6">
          <div className="rounded-3xl border border-orange-100 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FF6F00]">Controls</p>
                <h2 className="mt-2 text-2xl font-black text-gray-950">{selectedArtist?.name || 'Select an artist'}</h2>
              </div>
              {publishedSlug && (
                <Link to={`/epk/${publishedSlug}`} target="_blank" rel="noreferrer" className="btn-outline text-center">
                  Open public EPK
                </Link>
              )}
            </div>
            <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1.4fr]">
              <select value={theme} onChange={(event) => setTheme(event.target.value)} className="rounded-2xl border border-gray-200 px-4 py-3">
                {EPK_THEMES.map((option) => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
              <div className="flex flex-wrap gap-2">
                {EPK_SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => toggleLock(section.id)}
                    className={`rounded-full border px-3 py-2 text-xs font-black ${
                      lockedSections.includes(section.id)
                        ? 'border-purple-200 bg-purple-50 text-purple-700'
                        : 'border-gray-200 bg-white text-gray-600'
                    }`}
                  >
                    {lockedSections.includes(section.id) ? 'Locked: ' : 'Lock '} {section.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <button type="button" onClick={saveSettings} disabled={!selectedArtistId || Boolean(busyAction)} className="btn-outline">
                {busyAction === 'settings' ? 'Saving...' : 'Save Settings'}
              </button>
              <button type="button" onClick={regenerate} disabled={!selectedArtistId || Boolean(busyAction)} className="btn-primary">
                {busyAction === 'regenerate' ? 'Regenerating...' : 'Force Regenerate'}
              </button>
              <button type="button" onClick={publish} disabled={!selectedArtistId || Boolean(busyAction)} className="rounded-full bg-emerald-600 px-5 py-3 font-black text-white disabled:opacity-50">
                {busyAction === 'publish' ? 'Publishing...' : 'Publish Latest'}
              </button>
            </div>
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-gray-500">Current EPK preview</p>
            {loadingEpk ? (
              <p className="mt-4 rounded-2xl bg-gray-50 p-5 font-bold text-gray-500">Loading EPK...</p>
            ) : (
              <div className="mt-4 grid gap-5 lg:grid-cols-[220px_1fr]">
                {hero.image_url ? (
                  <img src={hero.image_url} alt="" className="aspect-[4/5] w-full rounded-3xl object-cover" />
                ) : (
                  <div className="flex aspect-[4/5] items-center justify-center rounded-3xl bg-gray-100 text-5xl font-black">K</div>
                )}
                <div>
                  <p className="text-sm font-black text-[#4A148C]">{hero.art_form || 'Artist'} {hero.city ? `- ${hero.city}` : ''}</p>
                  <h3 className="mt-2 text-4xl font-black text-gray-950">{hero.artist_name || selectedArtist?.name}</h3>
                  <p className="mt-3 max-w-3xl text-gray-600">{about.body}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {(highlights.image_urls || []).slice(0, 4).map((url) => (
                      <img key={url} src={url} alt="" className="h-20 w-20 rounded-2xl object-cover" />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-gray-500">Versions</p>
            <div className="mt-4 space-y-2">
              {(epkData?.versions || []).length === 0 ? (
                <p className="rounded-2xl bg-gray-50 p-5 font-bold text-gray-500">No generated versions yet.</p>
              ) : (
                epkData.versions.map((version) => (
                  <div key={version.id} className="flex flex-col gap-3 rounded-2xl border border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-black text-gray-950">{version.status || version.version?.status || 'draft'}</p>
                      <p className="text-sm text-gray-500">{version.generated_at || version.version?.generated_at || 'No date'}</p>
                    </div>
                    <button type="button" onClick={() => rollback(version.id)} disabled={Boolean(busyAction)} className="btn-outline">
                      {busyAction === `rollback:${version.id}` ? 'Rolling back...' : 'Rollback to this'}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminLivingEpk;
